// Code generated by master-scheme ServiceTypesGenerator - DO NOT EDIT
// Generator: /Users/victorvins/Projects/master-scheme/dsl/src/ts-api/ServiceTypesGenerator.scala

import { postEndpoint } from '@innolab-trader/tradovate-transport'
import type { ValidationError } from '@innolab-trader/tradovate-types'
import type { OrderAction } from '@innolab-trader/tradovate-types/src/enums/orderAction'
import type { OrderType } from '@innolab-trader/tradovate-types/src/enums/orderType'
import type { RejectedReason } from '@innolab-trader/tradovate-types/src/enums/rejectedReason'
import type { TimeInForce } from '@innolab-trader/tradovate-types/src/enums/timeInForce'

export interface PlaceOrder {
  accountSpec?: string
  accountId?: number
  clOrdId?: string
  action: OrderAction
  symbol: string
  orderQty: number
  orderType: OrderType
  price?: number
  stopPrice?: number
  maxShow?: number
  pegDifference?: number
  triggerQty?: number
  timeInForce?: TimeInForce
  expireTime?: string
  text?: string
  activationTime?: string
  admin?: boolean
  customTag50?: string
  isAutomated?: boolean
}

export interface PlaceOrderResult {
  failureReason?: RejectedReason
  failureText?: string
  orderId?: number
}

/**
 * Validate PlaceOrder request
 *
 * Follows Rust validation pattern with structured errors
 *
 * @param request - The request object to validate
 * @returns Array of validation errors, empty if valid
 *
 * @example
 * ```typescript
 * const errors = validatePlaceOrder(requestData)
 * if (errors.length > 0) {
 *   return { status: 400, errors }
 * }
 * ```
 */
export function validatePlaceOrder(request: PlaceOrder): ValidationError[] {
  const errors: ValidationError[] = []

  const req = request as unknown as Record<string, unknown>
  if (typeof req.accountSpec === 'string' && req.accountSpec.length > 64) {
    errors.push({
      field: 'accountSpec',
      code: 'too_long',
      message: 'accountSpec must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.clOrdId === 'string' && req.clOrdId.length > 64) {
    errors.push({
      field: 'clOrdId',
      code: 'too_long',
      message: 'clOrdId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (req.action === undefined || req.action === null) {
    errors.push({
      field: 'action',
      code: 'required',
      message: 'action is required'
    })
  }

  if (req.symbol === undefined || req.symbol === null) {
    errors.push({
      field: 'symbol',
      code: 'required',
      message: 'symbol is required'
    })
  }
  if (typeof req.symbol === 'string' && req.symbol.length > 64) {
    errors.push({
      field: 'symbol',
      code: 'too_long',
      message: 'symbol must not exceed 64 characters',
      argument: '64'
    })
  }
  if (typeof req.symbol === 'string' && req.symbol.trim() === '') {
    errors.push({
      field: 'symbol',
      code: 'required',
      message: 'symbol cannot be empty'
    })
  }

  if (req.orderQty === undefined || req.orderQty === null) {
    errors.push({
      field: 'orderQty',
      code: 'required',
      message: 'orderQty is required'
    })
  }

  if (req.orderType === undefined || req.orderType === null) {
    errors.push({
      field: 'orderType',
      code: 'required',
      message: 'orderType is required'
    })
  }

  if (typeof req.text === 'string' && req.text.length > 64) {
    errors.push({
      field: 'text',
      code: 'too_long',
      message: 'text must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.customTag50 === 'string' && req.customTag50.length > 64) {
    errors.push({
      field: 'customTag50',
      code: 'too_long',
      message: 'customTag50 must not exceed 64 characters',
      argument: '64'
    })
  }

  return errors
}

export const placeOrder = postEndpoint<PlaceOrder, PlaceOrderResult>('/order/placeOrder')
